import { Component, OnInit, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';
import { map, catchError, filter, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../../core/services/auth.service';
import { RoleService } from '../../../core/services/role.service';
import { MaintenanceService } from '../../../core/services/maintenance.service';
import { User } from '../../../core/models/user.interface';
import { AppState } from '../../../core/store';
import { selectUser } from '../../../core/store/auth';
import { logout } from '../../../core/store/auth/auth.actions';

declare var bootstrap: any;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private roleService = inject(RoleService);
  private destroy$ = new Subject<void>();
  private documentClickHandler?: (event: Event) => void;
  
  user$: Observable<User | null>;
  isAuthenticated$: Observable<boolean>;
  isAdmin$: Observable<boolean>;
  isMaintenanceMode$: Observable<boolean>;
  isDropdownOpen = false;
  isMobileMenuOpen = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private store: Store<AppState>,
    private maintenanceService: MaintenanceService
  ) {
    // Solo nel browser, accedi al store
    if (isPlatformBrowser(this.platformId)) {
      this.user$ = this.store.select(selectUser).pipe(
        catchError(() => of(null))
      );
      this.isAuthenticated$ = this.store.select(selectUser).pipe(
        map(user => !!user),
        catchError(() => of(false))
      );
      this.isAdmin$ = this.roleService.isAdmin().pipe(
        catchError(() => of(false))
      );
      this.isMaintenanceMode$ = this.maintenanceService.getMaintenanceStatus$().pipe(
        map(status => status?.isMaintenanceMode ?? false),
        catchError(() => of(false))
      );
    } else {
      // Durante il prerendering, usa valori di default
      this.user$ = of(null);
      this.isAuthenticated$ = of(false);
      this.isAdmin$ = of(false);
      this.isMaintenanceMode$ = of(false);
    }
  }

  ngOnInit(): void {
    // Chiudi la dropdown quando si clicca fuori
    if (isPlatformBrowser(this.platformId)) {
      this.documentClickHandler = (event: Event) => {
        const target = event.target as HTMLElement;
        const dropdownElement = target.closest('.nav-item.dropdown');
        if (!dropdownElement && this.isDropdownOpen) {
          this.closeDropdown();
        }
      };
      document.addEventListener('click', this.documentClickHandler);

      this.router.events
        .pipe(
          takeUntil(this.destroy$),
          filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        )
        .subscribe(() => {
          this.closeDropdown();
          this.isMobileMenuOpen = false;
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (isPlatformBrowser(this.platformId) && this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.closeDropdown();
      this.isMobileMenuOpen = false;
      // Usa l'action di logout che gestirà il refresh tramite l'effect
      this.store.dispatch(logout());
    }
  }

  navigateToProfile(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.closeDropdown();
      this.router.navigate(['/profilo']);
    }
  }

  getSubscriptionBadgeClass(subscription: string): string {
    switch (subscription) {
      case 'FREE':
        return 'bg-secondary';
      case 'SILVER':
        return 'bg-light text-dark border';
      case 'GOLD':
        return 'bg-warning text-dark';
      case 'PLATINUM':
        return 'bg-dark';
      default:
        return 'bg-primary';
    }
  }

  toggleMobileMenu(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    }
  }

  closeMobileMenu(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileMenuOpen = false;
    }
  }
}
